/**
 * Overlay input handlers — pause, disconnect, tutorial pause.
 */

import { Keys } from 'bonkjs';
import type { InputProvider } from '../../input';
import type { GameState } from '../../types';
import type { GameEffect } from '../effects';
import { transitionPhase } from '../effects';
import { justPressed } from '../input-edge';
import { DEFAULT_STAGE_ID } from '../../stages';

// ─── Pause Input ─────────────────────────────────────────────

export function handlePauseInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  if (justPressed(Keys.ArrowUp, input) || justPressed(Keys.W, input)) {
    state.pauseSelectIndex = Math.max(0, state.pauseSelectIndex - 1);
  }
  if (justPressed(Keys.ArrowDown, input) || justPressed(Keys.S, input)) {
    state.pauseSelectIndex = Math.min(1, state.pauseSelectIndex + 1);
  }
  if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
    if (state.pauseSelectIndex === 0) {
      // Resume
      state.paused = false;
    } else {
      // Main Menu
      state.paused = false;
      state.multiplayer = null;
      transitionPhase(state, 'title_screen', fx);
      fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
    }
  }
  if (justPressed(Keys.Escape, input)) {
    // ESC also resumes
    state.paused = false;
  }
}

// ─── Disconnect Overlay Input ─────────────────────────────────

export function handleDisconnectOverlayInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  const mp = state.multiplayer;
  if (!mp) return;

  if (mp.disconnectPhase === 'reconnecting') {
    // Client-side countdown fallback — if server grace expired without message
    if (Date.now() >= mp.disconnectCountdownEnd) {
      mp.disconnectPhase = 'gone';
      mp.connected = false;
      return;
    }
    // ESC: skip wait, go straight to "gone"
    if (justPressed(Keys.Escape, input)) {
      mp.disconnectPhase = 'gone';
      mp.connected = false;
      return;
    }
  } else if (mp.disconnectPhase === 'gone') {
    // Navigate between ONLINE MENU (0) and MAIN MENU (1)
    if (justPressed(Keys.ArrowLeft, input) || justPressed(Keys.A, input) ||
        justPressed(Keys.ArrowUp, input) || justPressed(Keys.W, input)) {
      mp.disconnectCursor = 0;
    }
    if (justPressed(Keys.ArrowRight, input) || justPressed(Keys.D, input) ||
        justPressed(Keys.ArrowDown, input) || justPressed(Keys.S, input)) {
      mp.disconnectCursor = 1;
    }
    // Confirm selection
    if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
      if (mp.disconnectCursor === 0) {
        // ONLINE MENU — disconnect + go to mode select with cursor on "online"
        state.multiplayer = null;
        state.menuSelectIndex = 0; // "ONLINE 1V1" is hero button at index 0
        transitionPhase(state, 'mode_select', fx);
        fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
      } else {
        // MAIN MENU
        state.multiplayer = null;
        transitionPhase(state, 'title_screen', fx);
        fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
      }
    }
    // ESC = MAIN MENU
    if (justPressed(Keys.Escape, input)) {
      state.multiplayer = null;
      transitionPhase(state, 'title_screen', fx);
      fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
    }
  }
}

// ─── Tutorial Pause Input ─────────────────────────────────────

/** Tutorial pause menu rows: RESUME, SKIP STEP, RESTART, MAIN MENU */
const TUTORIAL_PAUSE_ROW_COUNT = 4;

export function handleTutorialPauseInput(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  if (justPressed(Keys.ArrowUp, input) || justPressed(Keys.W, input)) {
    state.pauseSelectIndex = Math.max(0, state.pauseSelectIndex - 1);
  }
  if (justPressed(Keys.ArrowDown, input) || justPressed(Keys.S, input)) {
    state.pauseSelectIndex = Math.min(TUTORIAL_PAUSE_ROW_COUNT - 1, state.pauseSelectIndex + 1);
  }
  if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
    switch (state.pauseSelectIndex) {
      case 0: // Resume
        state.paused = false;
        break;
      case 1: // Skip Step
        state.paused = false;
        state.modeState.tutorialEdgeguardFreeze = false;
        // Advance tutorial step (handled by external controller via effect)
        fx.push({ type: 'tutorial_step_advance', step: state.modeState.tutorialStep + 1 });
        break;
      case 2: // Restart Tutorial
        state.paused = false;
        state.modeState.tutorialEdgeguardFreeze = false;
        fx.push({ type: 'tutorial_step_advance', step: -1 }); // -1 = restart signal
        break;
      case 3: // Main Menu
        state.paused = false;
        state.modeState.tutorialEdgeguardFreeze = false;
        transitionPhase(state, 'title_screen', fx);
        fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
        break;
    }
  }
  if (justPressed(Keys.Escape, input)) {
    state.paused = false;
  }
}
